import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  viewChild,
} from '@angular/core';
import {
  ACCOUNT_NUMBER_DIGIT_MAX,
  applyAccountEdit,
  maskAccount,
} from '@system-dyg/shared/utils';
import { UiInputComponent } from '../input/input.component';

/** Captura de segmentos. El `0` visible de un segmento vacío no es un valor escrito. */
@Component({
  selector: 'ui-account-number-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiInputComponent],
  templateUrl: './account-number-input.component.html',
  styleUrl: './account-number-input.component.scss',
})
export class UiAccountNumberInputComponent {
  readonly label = input.required<string>();
  readonly segments = input.required<readonly string[]>();
  readonly separator = input('-');
  readonly disabled = input(false);
  readonly required = input(false);
  readonly invalid = input(false);
  readonly message = input<string | undefined>(undefined);
  readonly showOptionalHint = input(false);

  readonly segmentsChange = output<string[]>();

  private inputEl: HTMLInputElement | null = null;
  private readonly field = viewChild(UiInputComponent);

  protected readonly masked = computed(() =>
    maskAccount(this.segments(), this.separator()),
  );

  protected readonly maxLength = computed(() => {
    const gaps = Math.max(this.segments().length - 1, 0);
    return ACCOUNT_NUMBER_DIGIT_MAX + gaps * this.separator().length;
  });

  protected onFocus(event: FocusEvent): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.inputEl = input;
    this.placeCaret(0, this.masked());
  }

  protected onValue(value: string): void {
    const previous = this.segments();
    const cursor = this.inputEl?.selectionStart ?? value.length;
    const edit = applyAccountEdit(
      previous,
      this.masked(),
      value,
      cursor,
      this.separator(),
    );
    const changed = edit.values.some((part, index) => part !== previous[index]);
    if (changed) this.segmentsChange.emit(edit.values);

    const masked = maskAccount(edit.values, this.separator());
    this.field()?.value.set(masked);
    this.placeCaret(edit.caret, masked);
  }

  private placeCaret(position: number, masked: string): void {
    const input = this.inputEl;
    if (!input) return;
    requestAnimationFrame(() => {
      if (input.value !== masked) input.value = masked;
      const caret = Math.max(0, Math.min(position, input.value.length));
      input.setSelectionRange(caret, caret);
    });
  }
}
